import React from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Car, Check, CheckCircle, ChevronRight, Database, Edit2, Eye, EyeOff, Info, Package, RefreshCw, Save, Wrench, Zap,
} from 'lucide-react';
import { Section } from './helpers';
import { TabLoader } from './TabLoader';
import RepairItem from './RepairItem';

const RepairsTab = ({
  editData, canEdit, inspection, saving, saveChanges,
  calculatedRepairCosts, repairsByCategory, selectedRepairs,
  toggleRepairSelection, selectAllRepairs, updateRepairCostOverride,
  getEffectiveRepairCost, editingRepairCost, setEditingRepairCost,
  savingRepairSelection, saveRepairSelection, updatingMasterCost,
  updateMasterRepairCost, getRepairKey, expandedCategories,
  toggleCategoryExpansion, addRepair, removeRepair, updateRepair,
  repairParts, partCategories, calculateRepairTotals,
  setSaveToMasterRepair, updateField, saveRepairToMaster,
}) => {
  return (
    <>
            <TabsContent value="repairs" className="space-y-3 mt-0">
              {!inspection?.id ? <TabLoader message="Loading repair estimates..." /> : (<>
              {/* Cost Summary Bar at Top */}
              {(calculatedRepairCosts.length > 0 || editData.repairs.length > 0) && (
                <div className={`rounded-lg p-3 text-white flex items-center justify-between transition-all ${selectedRepairs.size > 0 ? 'bg-gradient-to-r from-purple-600 to-indigo-600' : 'bg-gradient-to-r from-gray-400 to-gray-500'}`}>
                  <div className="flex items-center gap-4">
                    <div>
                      <p className={`text-xs ${selectedRepairs.size > 0 ? 'text-purple-200' : 'text-gray-200'}`}>Total Estimated Cost</p>
                      <p className={`text-2xl font-bold ${selectedRepairs.size === 0 ? 'line-through opacity-60' : ''}`}>
                        Rs.{(calculatedRepairCosts.filter(r => selectedRepairs.has(getRepairKey(r))).reduce((sum, r) => { const eff = getEffectiveRepairCost(r); return sum + (eff.cost || 0); }, 0) + editData.repairs.reduce((sum, r) => sum + (r.estimated_cost || 0), 0)).toLocaleString()}
                      </p>
                    </div>
                    <div className="h-8 w-px bg-purple-400"></div>
                    <div className={`text-xs ${selectedRepairs.size > 0 ? 'text-purple-200' : 'text-gray-200'}`}>
                      <p>Auto: {calculatedRepairCosts.length} items ({selectedRepairs.size} selected)</p>
                      <p>Manual: {editData.repairs.length} items</p>
                      <p>Replace: {calculatedRepairCosts.filter(r => selectedRepairs.has(getRepairKey(r)) && r.action === 'REPLACE').length} | Repair: {calculatedRepairCosts.filter(r => selectedRepairs.has(getRepairKey(r)) && r.action === 'REPAIR').length}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => selectAllRepairs(selectedRepairs.size < calculatedRepairCosts.length)} className="text-xs border-white/30 text-white hover:bg-white/10">
                      {selectedRepairs.size === calculatedRepairCosts.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Button size="sm" onClick={saveRepairSelection} disabled={savingRepairSelection} className="bg-white/20 hover:bg-white/30 text-white text-xs">
                      {savingRepairSelection ? <><RefreshCw className="h-3 w-3 mr-1 animate-spin" />Saving...</> : <><Save className="h-3 w-3 mr-1" />Save</>}
                    </Button>
                  </div>
                </div>
              )}
              {/* Auto-Calculated Repairs from Rules */}
              {calculatedRepairCosts.length > 0 && (
                <Section title="Auto-Detected Repairs" icon={Zap} defaultOpen={true} badge={`${calculatedRepairCosts.length} items`}>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">
                        Repairs automatically detected from Q&A answers using repair rules. Prices based on {inspection?.car_type || 'sedan'} pricing{inspection?.vehicle_make ? ` for ${inspection.vehicle_make}` : ''}.
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => selectAllRepairs(selectedRepairs.size < calculatedRepairCosts.length)}
                          className="text-xs"
                        >
                          {selectedRepairs.size === calculatedRepairCosts.length ? 'Deselect All' : 'Select All'}
                        </Button>
                        <Button
                          size="sm"
                          onClick={saveRepairSelection}
                          disabled={savingRepairSelection}
                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                        >
                          {savingRepairSelection ? (
                            <>
                              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="h-3 w-3 mr-1" />
                              Save Selection
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    <p className="text-xs text-gray-500">
                      <Eye className="h-3 w-3 inline mr-1" />
                      {selectedRepairs.size} of {calculatedRepairCosts.length} repairs selected for report
                    </p>
                    
                    {/* Repair Items Grouped by Category/Component */}
                    <div className="space-y-2">
                      {Object.entries(repairsByCategory).map(([category, repairs]) => {
                        const isExpanded = expandedCategories[category] || false;
                        const categoryTotal = repairs.reduce((sum, r) => {
                          const eff = getEffectiveRepairCost(r);
                          return sum + (eff.cost || 0);
                        }, 0);
                        const selectedInCategory = repairs.filter(r => selectedRepairs.has(getRepairKey(r))).length;
                        const replaceCount = repairs.filter(r => r.action === 'REPLACE').length;
                        const noneSelected = selectedInCategory === 0;
                        
                        return (
                          <div key={category} className={`border rounded-lg overflow-hidden transition-all ${noneSelected ? 'border-gray-100 opacity-40' : 'border-gray-200'}`}>
                            {/* Category Header - Accordion Toggle */}
                            <button
                              onClick={() => toggleCategoryExpansion(category)}
                              className={`w-full flex items-center justify-between p-3 transition-colors ${noneSelected ? 'bg-gray-50/50' : 'bg-gray-50 hover:bg-gray-100'}`}
                            >
                              <div className="flex items-center gap-3">
                                <ChevronRight className={`h-4 w-4 transition-transform ${noneSelected ? 'text-gray-300' : 'text-gray-500'} ${isExpanded ? 'rotate-90' : ''}`} />
                                <div className="text-left">
                                  <p className={`font-medium ${noneSelected ? 'text-gray-400' : 'text-gray-900'}`}>{category}</p>
                                  <p className={`text-xs ${noneSelected ? 'text-gray-300' : 'text-gray-500'}`}>
                                    {repairs.length} item{repairs.length !== 1 ? 's' : ''}
                                    {replaceCount > 0 && <span className={noneSelected ? 'text-gray-300 ml-1' : 'text-red-600 ml-1'}>({replaceCount} replace)</span>}
                                    {' • '}{selectedInCategory} selected
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className={`font-bold ${noneSelected ? 'text-gray-300 line-through' : 'text-gray-900'}`}>{activeCurrencySymbol()}{categoryTotal.toLocaleString()}</p>
                              </div>
                            </button>
                            
                            {/* Category Items - Collapsible */}
                            {isExpanded && (
                              <div className="p-2 space-y-2 bg-white">
                                {repairs.map((repair, idx) => {
                                  const repairKey = getRepairKey(repair);
                                  const isSelected = selectedRepairs.has(repairKey);
                                  const effectiveCost = getEffectiveRepairCost(repair);
                                  const isEditing = editingRepairCost === repairKey;
                                  
                                  return (
                                    <div key={idx} className={`p-3 rounded-lg border-l-4 transition-all ${
                                      !isSelected ? 'opacity-50 bg-gray-50 border-l-gray-400' :
                                      repair.action === 'REPLACE' ? 'bg-red-50 border-l-red-500' : 
                                      repair.priority === 'critical' || repair.priority === 1 ? 'bg-red-50 border-l-red-500' :
                                      repair.priority === 'high' || repair.priority === 2 ? 'bg-orange-50 border-l-orange-500' :
                                      'bg-amber-50 border-l-amber-500'
                                    }`}>
                                      <div className="flex items-start justify-between gap-3">
                                        {/* Selection Toggle */}
                                        <div className="flex items-center pt-1">
                                          <button
                                            onClick={() => toggleRepairSelection(repair)}
                                            data-testid={`repair-toggle-${repair.question_key || repair.question_id}`}
                                            className={`h-5 w-5 rounded border-2 flex items-center justify-center transition-colors ${
                                              isSelected 
                                                ? 'bg-blue-600 border-blue-600 text-white' 
                                                : 'border-gray-300 hover:border-blue-400'
                                            }`}
                                            title={isSelected ? 'Click to hide from report' : 'Click to show in report'}
                                          >
                                            {isSelected && <Check className="h-3 w-3" />}
                                          </button>
                                        </div>
                                        
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                              repair.action === 'REPLACE' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                            }`}>
                                              {repair.action}
                                            </span>
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                                              repair.priority === 'critical' || repair.priority === 1 ? 'bg-red-100 text-red-700' :
                                              repair.priority === 'high' || repair.priority === 2 ? 'bg-orange-100 text-orange-700' :
                                              'bg-gray-100 text-gray-600'
                                            }`}>
                                              {typeof repair.priority === 'number' 
                                                ? (repair.priority === 1 ? 'HIGH' : repair.priority === 2 ? 'MEDIUM' : 'LOW')
                                                : (repair.priority?.toUpperCase() || 'NORMAL')}
                                            </span>
                                            {!isSelected && (
                                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                                                <EyeOff className="h-3 w-3 inline mr-1" />Hidden
                                              </span>
                                            )}
                                            {effectiveCost.isOverridden && (
                                              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Modified</span>
                                            )}
                                          </div>
                                          <p className="font-semibold text-gray-900 mt-1">{repair.part_name}</p>
                                          {repair.part_number && <p className="text-xs text-gray-400">#{repair.part_number}</p>}
                                        </div>
                                        
                                        {/* Cost Display/Edit */}
                                        <div className="text-right flex-shrink-0">
                                          {isEditing ? (
                                            <div className="space-y-1">
                                              <div className="flex items-center gap-1">
                                                <span className="text-xs text-gray-500 w-10">Part:</span>
                                                <input
                                                  type="number"
                                                  value={effectiveCost.price}
                                                  onChange={(e) => updateRepairCostOverride(repair, 'price', e.target.value)}
                                                  className="w-16 px-1 py-0.5 text-xs border rounded"
                                                />
                                              </div>
                                              <div className="flex items-center gap-1">
                                                <span className="text-xs text-gray-500 w-10">Labor:</span>
                                                <input
                                                  type="number"
                                                  value={effectiveCost.labor}
                                                  onChange={(e) => updateRepairCostOverride(repair, 'labor', e.target.value)}
                                                  className="w-16 px-1 py-0.5 text-xs border rounded"
                                                />
                                              </div>
                                              <div className="flex gap-1 mt-1">
                                                <Button size="sm" variant="outline" onClick={() => setEditingRepairCost(null)} className="text-xs px-1 py-0.5 h-6">Done</Button>
                                                <Button size="sm" onClick={() => updateMasterRepairCost(repair)} disabled={updatingMasterCost === repair.part_id} className="text-xs px-1 py-0.5 h-6 bg-green-600 hover:bg-green-700 text-white">
                                                  {updatingMasterCost === repair.part_id ? <RefreshCw className="h-3 w-3 animate-spin" /> : <><Database className="h-3 w-3 mr-0.5" />Master</>}
                                                </Button>
                                              </div>
                                            </div>
                                          ) : (
                                            <>
                                              <p className={`text-lg font-bold ${effectiveCost.isOverridden ? 'text-purple-700' : 'text-gray-900'}`}>
                                                {activeCurrencySymbol()}{effectiveCost.cost?.toLocaleString()}
                                              </p>
                                              <div className="text-xs text-gray-500">
                                                {effectiveCost.price > 0 && <span>Part: {activeCurrencySymbol()}{effectiveCost.price?.toLocaleString()}</span>}
                                                {effectiveCost.labor > 0 && <span> Labor: {activeCurrencySymbol()}{effectiveCost.labor?.toLocaleString()}</span>}
                                              </div>
                                              <Button size="sm" variant="ghost" onClick={() => setEditingRepairCost(repairKey)} className="text-xs mt-1 h-6 text-blue-600 hover:text-blue-700">
                                                <Edit2 className="h-3 w-3 mr-1" />Edit
                                              </Button>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Compact inline total */}
                    <div className="bg-blue-50 rounded-lg p-2 border border-blue-200 flex items-center justify-between">
                      <span className="text-xs text-gray-600">Selected: <strong className="text-blue-700">Rs.{calculatedRepairCosts.filter(r => selectedRepairs.has(getRepairKey(r))).reduce((sum, r) => { const eff = getEffectiveRepairCost(r); return sum + (eff.cost || 0); }, 0).toLocaleString()}</strong></span>
                      <span className="text-xs text-gray-500">{selectedRepairs.size}/{calculatedRepairCosts.length} items</span>
                    </div>
                  </div>
                </Section>
              )}
              
              {/* Manual Repairs Section - Same row format as auto-detected */}
              {(editData.repairs.length > 0 || canEdit) && (
                <div className="border border-orange-200 rounded-lg overflow-hidden">
                  {/* Category Header matching auto-detected style */}
                  <button
                    onClick={() => toggleCategoryExpansion('manual-repairs')}
                    className="w-full flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <ChevronRight className={`h-4 w-4 text-orange-500 transition-transform ${(expandedCategories['manual-repairs'] !== false) ? 'rotate-90' : ''}`} />
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Manual Repairs</p>
                        <p className="text-xs text-gray-500">
                          {editData.repairs.length} item{editData.repairs.length !== 1 ? 's' : ''}
                          {' • '}Custom entries
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">{activeCurrencySymbol()}{editData.repairs.reduce((sum, r) => sum + (r.estimated_cost || 0), 0).toLocaleString()}</p>
                    </div>
                  </button>

                  {/* Expanded content */}
                  {(expandedCategories['manual-repairs'] !== false) && (
                    <div className="border-t border-orange-100">
                      {/* Compact row list of manual repairs */}
                      {editData.repairs.map((repair, idx) => (
                        <div key={idx} className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
                          {/* Index */}
                          <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                            repair.action === 'replace' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                          }`}>
                            {idx + 1}
                          </div>

                          {/* Item name + action badge */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-900 truncate">{repair.item || 'Unnamed item'}</p>
                              {repair.action && (
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase flex-shrink-0 ${
                                  repair.action === 'replace' ? 'bg-red-100 text-red-700' :
                                  repair.action === 'inspect_further' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {repair.action === 'inspect_further' ? 'inspect' : repair.action}
                                </span>
                              )}
                            </div>
                            {repair.category && (
                              <p className="text-xs text-gray-400">{repair.category}</p>
                            )}
                          </div>

                          {/* Price */}
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-bold text-gray-900">{activeCurrencySymbol()}{(repair.estimated_cost || 0).toLocaleString()}</p>
                            <div className="text-[10px] text-gray-400">
                              {repair.price > 0 && <span>P: {activeCurrencySymbol()}{repair.price?.toLocaleString()}</span>}
                              {repair.labor > 0 && <span className="ml-1">L: {activeCurrencySymbol()}{repair.labor?.toLocaleString()}</span>}
                            </div>
                          </div>

                          {/* Edit / Remove buttons */}
                          {canEdit && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700" onClick={() => toggleCategoryExpansion(`manual-edit-${idx}`)}>
                                <Edit2 className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => removeRepair(idx)}>
                                <span className="text-base leading-none">&times;</span>
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Expanded edit form for individual repairs */}
                      {editData.repairs.map((repair, idx) => (
                        expandedCategories[`manual-edit-${idx}`] && (
                          <div key={`edit-${idx}`} className="border-t border-orange-200 bg-orange-50/30 p-3">
                            <RepairItem
                              repair={repair}
                              index={idx}
                              onUpdate={updateRepair}
                              onRemove={removeRepair}
                              onSaveToMaster={saveRepairToMaster}
                              repairParts={repairParts}
                              partCategories={partCategories}
                              vehicleMake={inspection?.vehicle_make || ''}
                              carType={inspection?.car_type || 'sedan'}
                              canEdit={canEdit}
                            />
                            <Button size="sm" variant="outline" className="mt-2 text-xs" onClick={() => toggleCategoryExpansion(`manual-edit-${idx}`)}>Done editing</Button>
                          </div>
                        )
                      ))}

                      {/* Add button */}
                      {canEdit && (
                        <div className="p-3 border-t border-orange-100">
                          <Button variant="outline" onClick={addRepair} className="w-full border-dashed text-sm h-8 border-orange-300 text-orange-600 hover:bg-orange-50">
                            <Package className="h-3.5 w-3.5 mr-2" />
                            Add Repair Item
                          </Button>
                        </div>
                      )}

                      {/* Save button */}
                      {editData.repairs.length > 0 && canEdit && (
                        <div className="px-3 pb-3">
                          <Button 
                            size="sm" 
                            onClick={saveChanges} 
                            disabled={saving}
                            className="w-full bg-orange-600 hover:bg-orange-700 text-white text-xs h-8"
                            data-testid="save-manual-repairs-btn"
                          >
                            {saving ? <><RefreshCw className="h-3 w-3 mr-1 animate-spin" />Saving...</> : <><Save className="h-3 w-3 mr-1" />Save Manual Repairs</>}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* No Repairs */}
              {calculatedRepairCosts.length === 0 && editData.repairs.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
                  <p className="font-medium">No Repairs Detected</p>
                  <p className="text-sm">No repair requirements have been identified from the inspection</p>
                </div>
              )}
              </>)}
            </TabsContent>
            

    </>
  );
};
import { activeCurrencySymbol } from '@/utils/currency';

export default RepairsTab;
